import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import type { Brand } from '@seek/melways-sites';

import type { Environment } from '../types';

import { redactEmail } from './redactEmail';

export interface DatadogInitProps {
  language: string;
  locale: string;
  brand?: Brand;
  isMobileClient?: boolean;
  environment: Environment;
  version?: string;
  [key: string]: any;
}

export const isDataDogRUMEnabled = () =>
  datadogRum &&
  Number(datadogRum.getInitConfiguration()?.sessionSampleRate) > 0;

export const track = (e: Error | string | any): void => {
  if (isDataDogRUMEnabled()) {
    if (e instanceof Error) {
      datadogRum.addError(e);
    } else {
      datadogRum.addError({
        message: typeof e === 'string' ? e : 'Unknown error',
        source: 'custom',
        stack: typeof e === 'object' && e.stack ? e.stack : '',
      });
    }
  } else {
    // eslint-disable-next-line no-console
    console.error(e);
  }
};

export const logError = (error: unknown, context?: object | string): void => {
  if (isDataDogRUMEnabled()) {
    datadogRum.addError(
      error,
      typeof context === 'string' ? { message: context } : context,
    );
  } else {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};

export const beforeSend = (event: RumEvent) => {
  if (event.type === 'action' && event.action?.target?.name) {
    event.action.target.name = redactEmail(event.action.target.name);
  }

  if (event.type === 'error' && event.error?.message) {
    event.error.message = redactEmail(event.error.message);
  }

  return true;
};

export const configureErrorTracking = ({
  environment,
  ...context
}: DatadogInitProps) => {
  try {
    if (isDataDogRUMEnabled()) {
      return;
    }

    datadogRum.init({
      applicationId: '2ad38267-7e17-4409-91d6-f2d4c5748075',
      clientToken: 'pub5a198bb36dd4f7a8845da52d49de088a',
      site: 'datadoghq.com',
      service: 'online-identity-auth0-hosted-pages',
      env: environment,
      version: context.version,
      sessionSampleRate: 10,
      sessionReplaySampleRate: 2,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      beforeSend,
    });

    datadogLogs.init({
      clientToken: 'pub5a198bb36dd4f7a8845da52d49de088a',
      site: 'datadoghq.com',
      service: 'online-identity-auth0-hosted-pages',
      env: environment,
      version: context.version,
      sessionSampleRate: 10,
      forwardErrorsToLogs: true,
    });

    datadogRum.setGlobalContext(context);
    datadogLogs.setGlobalContext(context);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('An error occurred setting up error reporting', error);
  }
};
